import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Button, TextField, Grid, Paper, Box, Tooltip, DialogContentText } from "@material-ui/core";
import { Club } from "api/clubs";
import { useSnackbar } from "notistack";
import { Autocomplete } from "@material-ui/lab";
import { CheckIn, ContactPerson, getCheckInsVisitors, getCheckInsVisitorsOverlaps, postCheckInsVisitorsOverlapsExport, postCheckInsCaseNotifications } from "api/owner/checkins";
import { DateSelect, DefaultTable } from "components";
import { Column, Query } from "material-table";
import moment from "moment";
import i18n from "../i18n";

// prop types
interface Covid19DialogProps {
    club?: Club,
    show: boolean,
    onClose: () => void,
}

// covid-19 identify contacts dialog
const Covid19Dialog = ({ club, show, onClose }: Covid19DialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [isLoading, setLoading] = useState(false);
    const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
    const [fromDate, setFromDate] = useState<string>();
    const [hasNotified, setHasNotified] = useState(false);
    const [queryCheckIn, setQueryCheckIn] = useState<CheckIn | null>(null);
    const [showNotificationAlert, setShowNotificationAlert] = useState(false);

    // get visitors
    const fetchVisitors = async (query: string) => {
        setCheckIns((await getCheckInsVisitors({ clubId: club?.id, limit: 10, query })).data);
    };

    // export action
    const exportOverlapping = async () => {
        setLoading(true);
        try {
            const { url } = await postCheckInsVisitorsOverlapsExport({
                clubId: club!.id,
                fromDate: fromDate!,
                personIdentifier: queryCheckIn!.personIdentifier,
            });
            window.open(url);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // notify action
    const notifyContacts = async () => {
        setShowNotificationAlert(false);
        setLoading(true);
        try {
            await postCheckInsCaseNotifications({
                clubId: club!.id,
                fromDate: fromDate!,
                personIdentifier: queryCheckIn!.personIdentifier,
            });
            enqueueSnackbar(i18n.notifySuccess, { variant: "success" });
            setHasNotified(true);
        } catch (error) {
            displayError(error);
        }
        setLoading(false);
    };

    // table columns
    const tableColumns: Column<ContactPerson>[] = [
        {
            field: "contact",
            render: ({ checkIn }: { checkIn: CheckIn }) => (
                <>
                    <div>{checkIn.contactData.fullName}</div>
                    <div>{checkIn.contactData.street}</div>
                    <div>{`${checkIn.contactData.postalCode} ${checkIn.contactData.city}`}</div>
                    <div>{checkIn.contactData.telephoneNumber}</div>
                </>
            ),
            title: i18n.contact,
        },
        {
            field: "hadCloseContact",
            render: ({ hadCloseContact }) => (hadCloseContact ? i18n.yes : i18n.no),
            title: i18n.hadCloseContact,
        },
        {
            field: "time",
            render: ({ checkIn }) => `${moment(checkIn.startDate).format("DD.MM.YYYY HH:mm")} - ${moment(checkIn.endDate).format("HH:mm")}`,
            title: i18n.time,
        },
    ];

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<ContactPerson>) => {
        const params: Parameters<typeof getCheckInsVisitorsOverlaps>[0] = {
            clubId: club!.id,
            fromDate: fromDate!,
            limit: query.pageSize,
            offset: query.pageSize * query.page,
            personIdentifier: queryCheckIn!.personIdentifier,
        };
        return params;
    };

    // table ref for refresh
    const tableRef = React.useRef<any>(null);
    const refreshTable = () => { if (tableRef.current) tableRef.current.onQueryChange(); };

    // render
    return (
        <>
            <Dialog fullWidth open={show} onClose={onClose} maxWidth="md">
                <DialogTitle>{i18n.identifyContacts}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {/* query */}
                        <Grid item xs={8}>
                            <Autocomplete
                                getOptionLabel={(option) => `${option.contactData.fullName} (${option.contactData.street})`}
                                getOptionSelected={(option, value) => option.personIdentifier === value.personIdentifier}
                                options={checkIns}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={i18n.person}
                                        placeholder={i18n.personQuery}
                                    />
                                )}
                                value={queryCheckIn}
                                onChange={(e, v) => setQueryCheckIn(v)}
                                onInputChange={(e, v, r) => (r === "input") && fetchVisitors(v)}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <DateSelect
                                label={i18n.sinceDate}
                                value={fromDate}
                                onChange={(m) => setFromDate(m?.startOf("day").toISOString())}
                            />
                        </Grid>

                        {/* table */}
                        <Grid item xs={12}>
                            <DefaultTable
                                columns={tableColumns}
                                components={{
                                    Container: (props) => <Paper {...props} variant="outlined" />,
                                }}
                                data={(query) => new Promise((resolve) => {
                                    if (queryCheckIn && fromDate) {
                                        const params = convertQueryToParams(query);
                                        getCheckInsVisitorsOverlaps(params)
                                            .then((response) => {
                                                resolve({
                                                    ...response,
                                                    page: query.page,
                                                });
                                                setHasNotified(false);
                                            })
                                            .catch((err) => {
                                                displayError(err);
                                                resolve({ data: [], page: 0, totalCount: 0 });
                                            });
                                    } else {
                                        resolve({ data: [], page: 0, totalCount: 0 });
                                    }
                                })}
                                options={{ search: false, sorting: false }}
                                tableRef={tableRef}
                                title={i18n.contactPersons}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                {isLoading && <LinearProgress />}
                <DialogActions>
                    <Box style={{ marginRight: "auto" }}>
                        <Tooltip title={i18n.notifyCovidDesc}>
                            <Button onClick={() => setShowNotificationAlert(true)} color="secondary" disabled={isLoading || !queryCheckIn || !fromDate || hasNotified}>
                                {i18n.notify}
                            </Button>
                        </Tooltip>
                        <Button onClick={exportOverlapping} color="secondary" disabled={isLoading || !queryCheckIn || !fromDate}>
                            {i18n.export}
                        </Button>
                    </Box>
                    <Button color="secondary" onClick={onClose}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={refreshTable} disabled={isLoading || !queryCheckIn || !fromDate}>
                        {i18n.preview}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* notification alert */}
            <Dialog open={showNotificationAlert} onClose={() => setShowNotificationAlert(false)}>
                <DialogTitle>{i18n.areYouSure}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{i18n.notifyCovidConfirm}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowNotificationAlert(false)}>{i18n.cancel}</Button>
                    <Button color="primary" onClick={notifyContacts}>{i18n.notify}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Covid19Dialog;
